import { useState, useRef, useEffect } from 'react';
import { Upload, Play, Youtube, Image as ImageIcon, BookOpen, GraduationCap, Plus, Download, ExternalLink, FileText, Video, CircleDashed, Check, ListTree, MoreVertical, Edit2, Trash2, AlertTriangle, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useDeleteTopic } from '@/hooks/useDeleteTopic';
import { RenameTopicModal } from '@/components/topic/RenameTopicModal';
import { DeleteTopicModal } from '@/components/topic/DeleteTopicModal';
import LessonItem from './LessonItem';
import QuizItem from './QuizItem';
import SuggestedLessons from './SuggestedLessons';
import AddLessonInput from '@/components/topic/AddLessonInput';
import TopicOnboardingModal from '@/components/topic/TopicOnboardingModal';
import { Lesson, Quiz } from '@/types/course-outline';
import { useLessonSuggestions } from '@/hooks/useLessonSuggestions';
import { Button } from '@/components/ui/button';
import DocumentSelectionModal from '@/components/document/DocumentSelectionModal';
import { YouTubeUrlModal } from '@/components/youtube/YouTubeUrlModal';
import { ImageSelectionModal } from '@/components/image/ImageSelectionModal';
import { useTopicLibraryItems } from '@/hooks/useTopicLibraryItems';
import { useTopicOutline, TopicLesson, useTopic } from '@/hooks/useQueryHooks';
import { TopicVideo } from '@/hooks/useTopicVideos';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useScrollIndicator } from '@/hooks/useScrollIndicator';
import { useDocumentUpload } from '@/hooks/useDocumentUpload';

interface Library2Props {
  onUploadDocument?: (file: File) => Promise<void>;
  onYouTubeVideoAdd?: (videoId: string, title: string, embedUrl: string) => void;
  onImagesAdd?: (images: {
    id: string;
    file: File;
    blobUrl: string;
    name: string;
  }[]) => void;
  topicId?: string;
  onLessonClick?: (lessonData: TopicLesson) => void;
  onDocumentSelect?: (documentId: string, documentName: string) => void;
  onVideoSelect?: (video: TopicVideo) => void;
}

const Library2 = ({
  onUploadDocument,
  onYouTubeVideoAdd,
  onImagesAdd,
  topicId,
  onLessonClick,
  onDocumentSelect,
  onVideoSelect
}: Library2Props) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showAllReferences, setShowAllReferences] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [lessonModalMessage, setLessonModalMessage] = useState('');
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    itemId: string;
    itemType: 'document' | 'video';
    itemName: string;
  }>({
    isOpen: false,
    itemId: '',
    itemType: 'document',
    itemName: ''
  });
  const [isDeletingResource, setIsDeletingResource] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);
  const hasAnimatedRef = useRef<string | undefined>(undefined);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  
  const {
    isScrolling,
    handleScroll
  } = useScrollIndicator();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { deleteTopic, isDeleting } = useDeleteTopic();
  const navigate = useNavigate();
  const { uploadDocument, isUploading } = useDocumentUpload();
  
  // Track topic ID changes to trigger animation only on topic switch
  useEffect(() => {
    if (topicId && topicId !== hasAnimatedRef.current) {
      setShouldAnimate(true);
      hasAnimatedRef.current = topicId;
    } else {
      setShouldAnimate(false);
    }
  }, [topicId]);
  
  const { data: libraryItems = [], isLoading } = useTopicLibraryItems(topicId);
  const { data: outlineData, isLoading: outlineLoading } = useTopicOutline(topicId);
  const { data: topicData } = useTopic(topicId);
  const { data: suggestions } = useLessonSuggestions(topicId);
  
  const handleUploadDocument = async (files: File[]) => {
    try {
      for (const file of files) {
        if (onUploadDocument) {
          await onUploadDocument(file);
        } else {
          await uploadDocument(file, topicId);
        }
      }
      setShowUploadModal(false);
      queryClient.invalidateQueries({ queryKey: ['topic-library-items', topicId] });
    } catch (error) {
      console.error('Upload error:', error);
    }
  };
  
  const handleYouTubeVideoAdd = (videoId: string, title: string, embedUrl: string) => {
    if (onYouTubeVideoAdd) {
      onYouTubeVideoAdd(videoId, title, embedUrl);
    }
    setShowYouTubeModal(false);
  };
  
  const handleImagesAdd = (images: {
    id: string;
    file: File;
    blobUrl: string;
    name: string;
  }[]) => {
    if (onImagesAdd) {
      onImagesAdd(images);
    }
    setShowImageModal(false);
  };
  
  const handleAddLesson = (message: string) => {
    setLessonModalMessage(message);
    setShowLessonModal(true);
  };
  
  const handleRenameTopic = () => {
    setIsRenameModalOpen(true);
  };
  
  const handleDeleteTopic = () => {
    setIsDeleteModalOpen(true);
  };
  
  const handleRemoveItem = async (itemId: string, itemType: 'document' | 'video') => {
    if (!topicId || !user) return;
    
    setIsDeletingResource(true);
    try {
      if (itemType === 'document') {
        const { error } = await (supabase as any)
          .from('topic_documents')
          .delete()
          .eq('document_id', itemId)
          .eq('topic_id', topicId);
        
        if (error) throw error;
      } else if (itemType === 'video') {
        const { error } = await (supabase as any)
          .from('topic_videos')
          .delete()
          .eq('id', itemId);
        
        if (error) throw error;
      }
      
      queryClient.invalidateQueries({ queryKey: ['topic-library-items', topicId] });
      toast({
        title: "Success",
        description: `${itemType === 'document' ? 'Document' : 'Video'} removed from topic`,
      });
      setDeleteModalState({ isOpen: false, itemId: '', itemType: 'document', itemName: '' });
    } catch (error) {
      console.error('Error removing item:', error);
      toast({
        title: "Error",
        description: "Failed to remove item",
        variant: "destructive",
      });
    } finally {
      setIsDeletingResource(false);
    }
  };
  
  const handleRename = async (newTitle: string, newInstructions: string) => {
    if (!topicId || !user) return;
    try {
      const { error } = await supabase.from('topics').update({
        title: newTitle,
        instructions: newInstructions
      }).eq('id', topicId).eq('user_id', user.id);
      
      if (error) {
        toast({
          title: "Error",
          description: "Failed to update topic. Please try again.",
          variant: "destructive"
        });
        return;
      }
      
      toast({
        title: "Success",
        description: "Topic updated successfully."
      });
      
      queryClient.invalidateQueries({ queryKey: ['topic', topicId] });
      queryClient.invalidateQueries({ queryKey: ['topicOutline', topicId] });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update topic. Please try again.",
        variant: "destructive"
      });
    }
  };
  
  const handleConfirmDelete = async () => {
    if (!topicId || !user) return;
    try {
      await deleteTopic({
        topicId: topicId,
        topicTitle: outline?.course_title || topicData?.title || 'Untitled',
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          navigate('/');
        }
      });
    } catch (error) {
      console.error('Error deleting topic:', error);
    }
  };

  // Extract outline data
  const hasOutline = outlineData?.has_outline || false;
  const outline = outlineData?.outline;
  const lessons = outlineData?.lessons || [];
  const quizzes = outlineData?.quizzes || [];

  // Check if description overflows 115px
  useEffect(() => {
    if (descriptionRef.current && outline?.course_description) {
      const scrollHeight = descriptionRef.current.scrollHeight;
      setShowReadMore(scrollHeight > 115);
    }
  }, [outline?.course_description]);

  // Transform lessons to Lesson type for LessonItem component
  const transformedLessons: Lesson[] = lessons.map((lesson: TopicLesson) => ({
    id: lesson.id,
    title: lesson.title,
    lessonNumber: lesson.lesson_number,
    duration: `${lesson.duration_minutes || 15} minutes`,
    completed: lesson.completion_percentage === 100,
    completionPercentage: lesson.completion_percentage,
    imageUrl: lesson.lesson_image_url || undefined,
    status: lesson.status
  }));

  // Add polling for generating lessons
  useEffect(() => {
    const hasGeneratingLessons = lessons.some(l => l.status === 'generating' || l.status === 'pending');
    if (hasGeneratingLessons && topicId) {
      const interval = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ['topic-outline', topicId] });
      }, 3000); // Poll every 3 seconds
      
      return () => clearInterval(interval);
    }
  }, [lessons, topicId, queryClient]);

  // Transform quizzes to Quiz type for QuizItem component
  const transformedQuizzes: Quiz[] = quizzes.map((quiz: TopicLesson) => ({
    id: quiz.id,
    title: quiz.title,
    subtitle: quiz.description || '',
    duration: `${quiz.duration_minutes || 15} minutes`,
    completed: quiz.completion_percentage === 100,
    imageUrl: quiz.lesson_image_url || undefined
  }));

  return (
    <>
      <div className="overflow-auto" style={{
      paddingLeft: '6%',
      paddingRight: '6%',
      paddingTop: '24px',
      paddingBottom: '48px',
      scrollbarWidth: 'thin',
      scrollbarColor: isScrolling ? 'rgba(0, 0, 0, 0.2) transparent' : 'transparent transparent'
    }} onScroll={handleScroll}>
      <style dangerouslySetInnerHTML={{
        __html: `
          .overflow-auto::-webkit-scrollbar {
            width: 8px;
          }
          .overflow-auto::-webkit-scrollbar-track {
            background: transparent;
          }
          .overflow-auto::-webkit-scrollbar-thumb {
            background: ${isScrolling ? 'rgba(0, 0, 0, 0.2)' : 'transparent'};
            border-radius: 10px;
            transition: background 0.3s ease;
          }
        `
      }} />
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header Row */}
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-2xl font-heading font-medium text-foreground">
            {outline?.course_title || topicData?.title || 'Untitled'}
          </h1>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="p-1 w-7 h-7 hover:bg-gray-100 rounded-lg transition-all duration-200 border-0 focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none data-[state=open]:bg-gray-100">
                <MoreVertical className="w-4 h-4 text-gray-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40 bg-white z-50">
              <DropdownMenuItem onClick={handleRenameTopic} className="cursor-pointer">
                <Edit2 className="w-4 h-4 mr-2" />
                Update
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDeleteTopic} className="cursor-pointer text-red-600 focus:text-red-600">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Course Overview Section */}
        {/* <div className="pb-8 border-b border-[#EDEDED]">
          <div className="flex gap-8">
            <div className="w-[180px] h-[140px] bg-muted rounded-xl flex-shrink-0 overflow-hidden">
              <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center">
                <BookOpen className="w-14 h-14 text-amber-600" />
              </div>
            </div>
            <div className="flex-1">
              <div className="relative">
                {outline?.course_description ? (
                  <div className={isDescriptionExpanded ? '' : 'relative'}>
                    <p ref={descriptionRef} className={`text-[13px] text-muted-foreground leading-[1.6] transition-all duration-300 ${isDescriptionExpanded ? '' : 'max-h-[140px] overflow-hidden'}`}>
                      {outline.course_description}
                    </p>
                    {showReadMore && !isDescriptionExpanded && (
                      <div className="absolute bottom-0 right-0 pl-2 bg-transparent">
                        <span className="text-[13px] text-muted-foreground leading-[1.6]">... </span>
                        <button onClick={() => setIsDescriptionExpanded(true)} className="text-[13px] text-muted-foreground hover:text-primary underline transition-colors leading-[1.6]">
                          Read more
                        </button>
                      </div>
                    )}
                    {showReadMore && isDescriptionExpanded && (
                      <button onClick={() => setIsDescriptionExpanded(false)} className="text-[14px] text-muted-foreground hover:text-primary underline mt-2 transition-colors leading-[1.6]">
                        Read less
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-[13px] text-muted-foreground/60 leading-[1.6] italic">
                    Add resources below to generate a course outline
                  </p>
                )}
              </div>
            </div>
          </div>
        </div> */}

        {/* References Section */}
        <div className="space-y-4 pb-8 border-b border-[#EDEDED]">
          <div className="flex items-center justify-between">
            <h2 className="text-lg italic text-muted-foreground font-heading font-semibold">References</h2>
            
            {libraryItems.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 border-muted-foreground/20 hover:bg-[#F4F3F1]">
                    <Plus className="w-4 h-4" />
                    Add Resources
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => setShowUploadModal(true)} className="gap-2 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Upload Files
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowYouTubeModal(true)} className="gap-2 cursor-pointer">
                    <Play className="w-4 h-4" />
                    Youtube
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowImageModal(true)} className="gap-2 cursor-pointer">
                    <ImageIcon className="w-4 h-4" />
                    Images
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          
          {libraryItems.length === 0 ? (
            <div className="flex gap-4 mb-4 h-[80px] w-full">
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex-1 bg-white border border-[#d4d4d4] rounded-2xl p-4 cursor-pointer transition-all duration-200 flex flex-col items-start justify-center gap-2"
              >
                <Upload className="w-6 h-6 text-[#666666]" strokeWidth={2} />
                <span
                  className="font-medium text-[14px] leading-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    color: 'rgba(0,0,0,0.6)'
                  }}
                >
                  Upload Files
                </span>
              </button>

              <button
                onClick={() => setShowYouTubeModal(true)}
                className="flex-1 bg-white border border-[#d4d4d4] rounded-2xl p-4 cursor-pointer transition-all duration-200 flex flex-col items-start justify-center gap-2"
              >
                <Youtube className="w-6 h-6 text-[#666666]" strokeWidth={2} />
                <span
                  className="font-medium text-[14px] leading-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    color: 'rgba(0,0,0,0.6)'
                  }}
                >
                  Youtube
                </span>
              </button>

              <button
                onClick={() => setShowImageModal(true)}
                className="flex-1 bg-white border border-[#d4d4d4] rounded-2xl p-4 cursor-pointer transition-all duration-200 flex flex-col items-start justify-center gap-2"
              >
                <ImageIcon className="w-6 h-6 text-[#666666]" strokeWidth={2} />
                <span
                  className="font-medium text-[14px] leading-none"
                  style={{
                    fontFamily: 'Lexend Deca',
                    color: 'rgba(0,0,0,0.6)'
                  }}
                >
                  Images
                </span>
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-5 gap-3">
                {(showAllReferences ? libraryItems : libraryItems.slice(0, 15)).map(item => (
                  <button 
                    key={item.id} 
                    onClick={() => {
                      if (item.type === 'document') {
                        onDocumentSelect?.(item.id, item.title);
                      } else if (item.type === 'video') {
                        const video: TopicVideo = {
                          id: item.id,
                          video_id: item.video_id || '',
                          title: item.title,
                          embed_url: item.embed_url || '',
                          duration: item.duration,
                          thumbnail_url: item.thumbnail_url,
                          created_at: item.created_at,
                          topic_id: topicId || '',
                          user_id: '',
                          has_chapters: false,
                          language: '',
                          total_segments: 0,
                          updated_at: item.created_at
                        };
                        onVideoSelect?.(video);
                      }
                    }} 
                    className="relative bg-white border border-border rounded-xl overflow-hidden transition-all group text-left"
                  >
                    {/* Delete Button - Shows on Hover */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteModalState({
                          isOpen: true,
                          itemId: item.id,
                          itemType: item.type,
                          itemName: item.title
                        });
                      }}
                      className="absolute top-2 right-2 bg-white/90 hover:bg-red-50 rounded-lg p-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>

                    <div className="aspect-[4/3] bg-muted overflow-hidden flex items-center justify-center">
                      {item.thumbnail_url ? (
                        <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                          {item.type === 'document' ? <FileText className="w-8 h-8 text-gray-400" /> : <Video className="w-8 h-8 text-gray-400" />}
                        </div>
                      )}
                      {item.status === 'processing' && (
                        <div className="absolute top-2 right-2 bg-yellow-500/90 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                          <span className="inline-block w-2 h-2 bg-white rounded-full animate-pulse" />
                          <span>Processing</span>
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-xs font-medium text-foreground line-clamp-1 mb-0.5">{item.title}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {item.type === 'document' ? 'PDF' : 'Youtube'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              
              {libraryItems.length > 15 && !showAllReferences && (
                <button onClick={() => setShowAllReferences(true)} className="text-sm text-muted-foreground hover:text-foreground underline mt-2">
                  View all references (+{libraryItems.length - 15})
                </button>
              )}
            </>
          )}
        </div>

        {/* Lessons Section */}
        <div className="space-y-4 pb-8 border-b border-[#EDEDED]">
          <div className="flex items-center justify-between">
            <h2 className="text-lg italic text-muted-foreground font-heading font-semibold">Lessons</h2>
            
            <Button 
              variant="outline" 
              size="sm" 
              className="gap-2 border-muted-foreground/20 hover:bg-[#F4F3F1]"
              onClick={() => {
                setLessonModalMessage('');
                setShowLessonModal(true);
              }}
            >
              <Plus className="w-4 h-4" />
              New Lesson
            </Button>
          </div>
          
          {transformedLessons.length > 0 ? (
            <div className="flex flex-col gap-3" style={{ fontFamily: 'Lexend Deca' }}>
              {transformedLessons.map(lesson => (
                <LessonItem 
                  key={lesson.id} 
                  lesson={lesson} 
                  onClick={() => {
                    const lessonData = lessons.find(l => l.id === lesson.id);
                    if (lessonData && onLessonClick) {
                      onLessonClick(lessonData);
                    }
                  }} 
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground/60 italic">
              No lessons yet. Generate an outline to create lessons.
            </p>
          )}
        </div>

        {/* Explore More Section */}
        {transformedLessons.length > 0 && (
          <div className="space-y-4 pb-8 border-b border-[#EDEDED]">
            <h2 className="text-lg italic text-muted-foreground font-heading font-semibold">Explore More</h2>
            
            {/* Add Lesson Input */}
            <AddLessonInput onSubmit={handleAddLesson} />
            
            {/* Suggested Lessons */}
            {suggestions && (
              <SuggestedLessons 
                suggestions={suggestions}
                onSuggestionClick={(title) => handleAddLesson(`Create a lesson: ${title}`)}
              />
            )}
          </div>
        )}
      </div>
    </div>

    {/* Modals - Rendered outside scrollable container for proper overlay */}
    {/* Upload Document Modal */}
    <DocumentSelectionModal 
      isOpen={showUploadModal} 
      onClose={() => setShowUploadModal(false)} 
      onDocumentsAdd={handleUploadDocument}
    />

    {/* YouTube URL Modal */}
    <YouTubeUrlModal isOpen={showYouTubeModal} onClose={() => setShowYouTubeModal(false)} onVideoAdd={handleYouTubeVideoAdd} />
    
    {/* Image Selection Modal */}
    <ImageSelectionModal isOpen={showImageModal} onClose={() => setShowImageModal(false)} onImagesAdd={handleImagesAdd} />
    
    {/* Topic Management Modals */}
    <RenameTopicModal 
      open={isRenameModalOpen} 
      onOpenChange={setIsRenameModalOpen} 
      currentTitle={outline?.course_title || topicData?.title || 'Untitled'} 
      currentInstructions={outline?.course_description || topicData?.instructions || ''} 
      onConfirm={handleRename} 
      isLoading={false} 
    />

    <DeleteTopicModal 
      open={isDeleteModalOpen} 
      onOpenChange={setIsDeleteModalOpen} 
      topicTitle={outline?.course_title || topicData?.title || 'Untitled'} 
      onConfirm={handleConfirmDelete} 
      isLoading={isDeleting} 
    />
    
    {/* Add Lesson Modal */}
    <TopicOnboardingModal 
      open={showLessonModal}
      onOpenChange={setShowLessonModal}
      initialMessage={lessonModalMessage}
      topicId={topicId}
      onFlowComplete={() => {
        setShowLessonModal(false);
        setLessonModalMessage('');
        queryClient.invalidateQueries({ queryKey: ['topic-outline', topicId] });
      }}
    />
    
    {/* Delete Resource Confirmation Modal */}
    {deleteModalState.isOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-slate-900">
                Delete {deleteModalState.itemType === 'document' ? 'Document' : 'Video'}?
              </h3>
            </div>
            <button
              onClick={() => setDeleteModalState({ isOpen: false, itemId: '', itemType: 'document', itemName: '' })}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              disabled={isDeletingResource}
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Are you sure you want to remove <span className="font-semibold text-slate-900">"{deleteModalState.itemName}"</span> from this topic? This action cannot be undone.
            </p>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteModalState({ isOpen: false, itemId: '', itemType: 'document', itemName: '' })}
                disabled={isDeletingResource}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleRemoveItem(deleteModalState.itemId, deleteModalState.itemType)}
                disabled={isDeletingResource}
                className="flex-1"
              >
                {isDeletingResource ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Library2;
